/**
 * French for the web app's screens. Keys are the English texts passed to `t()`;
 * a missing key simply shows the English text. Labels and messages shared with
 * the API are in packages/shared/src/locales/fr.ts.
 */
export const FR: Record<string, string> = {
  // ─── Common ────────────────────────────────────────────────────────────────
  Home: 'Accueil',
  Map: 'Carte',
  Meetups: 'Rencontres',
  Main: 'Navigation principale',
  Account: 'Compte',
  Admin: 'Admin',
  Close: 'Fermer',
  Loading: 'Chargement',
  'Try again': 'Réessayer',
  Save: 'Enregistrer',
  Change: 'Modifier',
  Required: 'Obligatoire',
  Optional: 'Facultatif',
  Note: 'Note',
  Name: 'Nom',
  Status: 'Statut',
  Badge: 'Badge',
  Role: 'Rôle',
  Car: 'Voiture',
  Phone: 'Téléphone',
  Password: 'Mot de passe',
  'Full name': 'Nom complet',
  'Make, model, year': 'Marque, modèle, année',
  '8 characters min.': '8 caractères min.',
  'Valid until': "Valable jusqu'au",
  'Member since': 'Membre depuis',
  'Badge {badge}': 'Badge {badge}',
  'valid until {date}': "valable jusqu'au {date}",
  On: 'Activé',
  Off: 'Désactivé',
  Online: 'En ligne',
  'Just now': "À l'instant",
  '{time} ago': 'il y a {time}',
  '{size} MB': '{size} Mo',
  'Good morning,': 'Bonjour,',
  'Good afternoon,': 'Bon après-midi,',
  'Good evening,': 'Bonsoir,',

  // ─── Welcome, sign in, join ────────────────────────────────────────────────
  'Car community': 'Communauté automobile',
  'Get started': 'Commencer',
  'Sign in': 'Se connecter',
  'Sign out': 'Se déconnecter',
  'New to Identity?': 'Nouveau chez Identity ?',
  'Request membership': 'Demander une adhésion',
  'Forgot your password? An admin can give you a temporary one.':
    'Mot de passe oublié ? Un admin peut vous en donner un temporaire.',
  'Join Identity': 'Rejoindre Identity',
  'Membership is validated by the club. Your badge is issued once your entry fee is confirmed.':
    "L'adhésion est validée par le club. Votre badge est émis une fois vos frais d'entrée confirmés.",
  'Create your account': 'Créez votre compte',
  'Name, phone, your car': 'Nom, téléphone, votre voiture',
  'Pay the entry fee · {amount}': "Payez les frais d'entrée · {amount}",
  'Upload a payment proof or pay in person': 'Envoyez un justificatif ou payez en main propre',
  'Get verified & receive your badge': 'Validation et réception de votre badge',
  'Then {amount} {frequency}': 'Puis {amount} {frequency}',
  'Submit request': 'Envoyer la demande',
  'This page does not exist.': "Cette page n'existe pas.",
  'Back to the app': "Retour à l'application",

  // ─── Install ───────────────────────────────────────────────────────────────
  'Install the Identity app': "Installer l'application Identity",
  'Install Identity': 'Installer Identity',
  Install: 'Installer',
  'Install the app': "Installer l'application",
  'Add it to your Home Screen': "Ajoutez-la à votre écran d'accueil",
  'Full screen, from your home screen': "En plein écran, depuis votre écran d'accueil",
  'Add it to your Dock': 'Ajoutez-la à votre Dock',
  'Open this page in your browser to install': 'Ouvrez cette page dans votre navigateur pour installer',
  'Full screen, one tap away': 'En plein écran, à portée de doigt',
  'Install on iPhone': 'Installer sur iPhone',
  '1. Tap Share': '1. Touchez Partager',
  'The square with an arrow, in the Safari toolbar': "Le carré avec une flèche, dans la barre d'outils de Safari",
  '2. Add to Home Screen': "2. Sur l'écran d'accueil",
  "Scroll the menu if you don't see it": 'Faites défiler le menu si vous ne le voyez pas',
  '3. Tap Add': '3. Touchez Ajouter',
  'Identity appears on your home screen': "Identity apparaît sur votre écran d'accueil",
  'The app then opens full screen, like a native app.':
    "L'application s'ouvre ensuite en plein écran, comme une application native.",
  'Install on Android': 'Installer sur Android',
  '1. Open the browser menu': '1. Ouvrez le menu du navigateur',
  'The ⋮ button, top right': 'Le bouton ⋮, en haut à droite',
  '2. Install app': "2. Installer l'application",
  'Or "Add to Home screen", depending on the browser': "Ou « Ajouter à l'écran d'accueil », selon le navigateur",
  'The app then opens full screen from your home screen.':
    "L'application s'ouvre ensuite en plein écran depuis votre écran d'accueil.",
  'Add to your Dock': 'Ajouter à votre Dock',
  '1. In Safari, open the File menu': '1. Dans Safari, ouvrez le menu Fichier',
  'Or the Share button of the toolbar': "Ou le bouton Partager de la barre d'outils",
  '2. Add to Dock': '2. Ajouter au Dock',
  'Identity then opens in its own window.': "Identity s'ouvre ensuite dans sa propre fenêtre.",
  'Open in your browser': 'Ouvrir dans votre navigateur',
  '1. Tap ⋯ in the corner': '1. Touchez ⋯ dans le coin',
  'This page is open inside another app': 'Cette page est ouverte dans une autre application',
  '2. Open in browser': '2. Ouvrir dans le navigateur',
  'Safari on iPhone, Chrome on Android': 'Safari sur iPhone, Chrome sur Android',
  '3. Install from there': '3. Installez depuis le navigateur',
  'Apps like Instagram or Facebook cannot install web apps themselves.':
    "Les applications comme Instagram ou Facebook ne peuvent pas installer d'applications web.",
  'Look for "Install" in your browser': 'Cherchez « Installer » dans votre navigateur',
  'In the address bar or the browser menu': "Dans la barre d'adresse ou le menu du navigateur",
  'Chrome, Edge and Safari can install Identity as an app.':
    'Chrome, Edge et Safari peuvent installer Identity comme une application.',

  // ─── Home ──────────────────────────────────────────────────────────────────
  Notifications: 'Notifications',
  'My account': 'Mon compte',
  'Show my pass QR code': 'Afficher le QR code de mon pass',
  'See all': 'Tout voir',
  'Next meetup': 'Prochaine rencontre',
  'Plan a meetup': 'Planifier une rencontre',
  'Nothing on the calendar yet': 'Rien au calendrier pour le moment',
  'No meetup planned yet. You will be notified when one is.':
    'Aucune rencontre prévue pour le moment. Vous serez prévenu dès la prochaine.',
  'Share location': 'Partager ma position',
  'Payment proof': 'Justificatif de paiement',
  'Club rules': 'Règles du club',
  'Club admin': 'Admin du club',
  'Members, payments, check-in': "Membres, paiements, contrôle à l'entrée",
  '{count} payment to review': '{count} paiement à vérifier',
  '{count} payments to review': '{count} paiements à vérifier',
  '{count} request': '{count} demande',
  '{count} requests': '{count} demandes',
  'Your membership is suspended. Contact an admin for details.':
    'Votre adhésion est suspendue. Contactez un admin pour plus de détails.',
  'Your membership request was not approved. Contact the club for details.':
    "Votre demande d'adhésion n'a pas été approuvée. Contactez le club pour plus de détails.",
  'Account created': 'Compte créé',
  'Entry fee sent': "Frais d'entrée envoyés",
  'Awaiting the treasurer': 'En attente du trésorier',
  'Pay entry fee': "Payer les frais d'entrée",
  'Then the member map and secret meetups open': "La carte des membres et les rencontres secrètes s'ouvrent ensuite",
  'Membership expired': 'Adhésion expirée',
  'Pay your dues to keep access': "Payez votre cotisation pour garder l'accès",
  Pay: 'Payer',
  Club: 'Club',
  'Staff only': 'Équipe uniquement',
  'New announcement': 'Nouvelle annonce',
  Announcements: 'Annonces',
  Announcement: 'Annonce',
  'News from the organizers will show up here.': 'Les nouvelles des organisateurs apparaîtront ici.',
  'Announcement posted': 'Annonce publiée',
  'Announcement deleted': 'Annonce supprimée',
  Message: 'Message',
  'Share news with the club…': 'Partagez une nouvelle avec le club…',
  Audience: 'Destinataires',
  Post: 'Publier',
  'Delete announcement': "Supprimer l'annonce",
  'All caught up': 'Tout est à jour',
  'Payments, meetups and club news will show up here.': 'Paiements, rencontres et nouvelles du club apparaîtront ici.',

  // ─── Account ───────────────────────────────────────────────────────────────
  'Change my profile photo': 'Changer ma photo de profil',
  'Pass & membership': 'Pass et adhésion',
  'Fees, payments, QR code': 'Tarifs, paiements, QR code',
  Profile: 'Profil',
  'Edit profile': 'Modifier le profil',
  'My car': 'Ma voiture',
  'Add your car and its photos': 'Ajoutez votre voiture et ses photos',
  Preferences: 'Préférences',
  Language: 'Langue',
  Appearance: 'Apparence',
  Dark: 'Sombre',
  Light: 'Clair',
  Automatic: 'Automatique',
  'Follows the setting of your phone': 'Suit le réglage de votre téléphone',
  'Notifications are written in this language too.': 'Les notifications sont aussi rédigées dans cette langue.',
  'Change password': 'Changer le mot de passe',
  Session: 'Session',
  'Sign out on all devices': 'Se déconnecter de tous les appareils',
  'Profile photo': 'Photo de profil',
  'Profile photo updated': 'Photo de profil mise à jour',
  'Profile photo removed': 'Photo de profil supprimée',
  'Choose a photo': 'Choisir une photo',
  'Choose another photo': 'Choisir une autre photo',
  'Remove the photo': 'Supprimer la photo',
  'Your photo is shown to the members of the club. It is cropped to a square.':
    'Votre photo est visible par les membres du club. Elle est recadrée en carré.',
  'This photo cannot be read. Try another one (JPG or PNG).': 'Photo illisible. Essayez-en une autre (JPG ou PNG).',
  'Profile updated': 'Profil mis à jour',
  'Password changed. Other devices are signed out.': 'Mot de passe changé. Les autres appareils sont déconnectés.',
  Current: 'Actuel',
  New: 'Nouveau',
  'Car updated': 'Voiture mise à jour',
  'Photos · {count} of {max}': 'Photos · {count} sur {max}',
  'Main photo': 'Photo principale',
  'Add a photo': 'Ajouter une photo',
  'Sending…': 'Envoi…',
  'The first photo is the main one. Car photos are shown to active members only.':
    'La première photo est la principale. Les photos de voiture sont visibles par les membres actifs uniquement.',
  'View the photo': 'Voir la photo',
  Photo: 'Photo',

  // ─── Push notifications ────────────────────────────────────────────────────
  'Turn on notifications': 'Activer les notifications',
  'Meetups, payments and club news': 'Rencontres, paiements et nouvelles du club',
  'Turn on': 'Activer',
  Blocked: 'Bloquées',
  'Not available': 'Indisponibles',
  'Notifications are not available in this browser.': 'Les notifications ne sont pas disponibles dans ce navigateur.',
  'Notifications are blocked. Allow them in the settings of your phone or browser.':
    'Les notifications sont bloquées. Autorisez-les dans les réglages de votre téléphone ou de votre navigateur.',
  'Notifications were not turned on.': "Les notifications n'ont pas été activées.",
  'This device receives a notification for meetups, payments, your membership and club news.':
    'Cet appareil reçoit une notification pour les rencontres, les paiements, votre adhésion et les nouvelles du club.',
  'Get a notification on this device for meetups, payments, your membership and club news.':
    'Recevez une notification sur cet appareil pour les rencontres, les paiements, votre adhésion et les nouvelles du club.',
  'On iPhone and iPad, notifications work in the installed app: add Identity to your Home Screen (Share › Add to Home Screen), then open it from there.':
    "Sur iPhone et iPad, les notifications fonctionnent dans l'application installée : ajoutez Identity à votre écran d'accueil (Partager › Sur l'écran d'accueil), puis ouvrez-la depuis l'icône.",
  'Notifications are blocked for Identity. Allow them in the settings of your phone (Settings › Notifications › Identity) or of your browser, then come back.':
    'Les notifications sont bloquées pour Identity. Autorisez-les dans les réglages de votre téléphone (Réglages › Notifications › Identity) ou de votre navigateur, puis revenez ici.',
  'This browser cannot receive notifications. Install the app, or use Chrome, Edge, Firefox or Safari.':
    "Ce navigateur ne peut pas recevoir de notifications. Installez l'application, ou utilisez Chrome, Edge, Firefox ou Safari.",
  'Send a test notification': 'Envoyer une notification de test',
  'Test sent to {count} device': 'Test envoyé à {count} appareil',
  'Test sent to {count} devices': 'Test envoyé à {count} appareils',
  'Turn off on this device': 'Désactiver sur cet appareil',
  'Notifications are off on this device': 'Notifications désactivées sur cet appareil',

  // ─── Club chat ─────────────────────────────────────────────────────────────
  Chat: 'Chat',
  '{count} unread': '{count} non lus',
  'Active members talk here: meetups, help with a car, photos and club news.':
    "Les membres actifs discutent ici : rencontres, coups de main mécanique, photos et nouvelles du club.",
  Today: "Aujourd'hui",
  'No messages yet': 'Aucun message pour le moment',
  'Say hello to the club! Every active member can read and write here.':
    'Dites bonjour au club ! Tous les membres actifs peuvent lire et écrire ici.',
  'Load earlier messages': 'Afficher les messages précédents',
  'This message was deleted': 'Ce message a été supprimé',
  'Deleted message': 'Message supprimé',
  'Message deleted': 'Message supprimé',
  'Mention a member': 'Mentionner un membre',
  'Replying to {name}': 'Réponse à {name}',
  'Cancel the reply': 'Annuler la réponse',
  'Profile of {name}': 'Profil de {name}',
  'Seen by': 'Vu par',
  'Seen by {count} member': 'Vu par {count} membre',
  'Seen by {count} members': 'Vu par {count} membres',
  'Nobody has seen this message yet.': "Personne n'a encore vu ce message.",
  'Members who opened the chat after this message was sent.': "Les membres qui ont ouvert le chat après l'envoi de ce message.",
  Reply: 'Répondre',
  Delete: 'Supprimer',
  Send: 'Envoyer',
  'Could not copy the message.': "Impossible de copier le message.",
  'Connecting…': 'Connexion…',
  'Chat notifications': 'Notifications du chat',
  'All messages': 'Tous les messages',
  'Mentions and replies': 'Mentions et réponses',
  Mentions: 'Mentions',
  'Grouped: one notification at most every {minutes} minutes': 'Regroupés : une notification au plus toutes les {minutes} minutes',
  'When someone writes @your name or replies to you': 'Quand quelqu’un écrit @votre nom ou vous répond',
  'Never for the chat': 'Jamais pour le chat',
  'Nothing is sent while you are reading the chat.': "Rien n'est envoyé pendant que vous lisez le chat.",
  'Turn on notifications for this device too (Notifications, just above).':
    'Activez aussi les notifications sur cet appareil (Notifications, juste au-dessus).',

  // ─── Profile: cover, bio, Instagram ────────────────────────────────────────
  Photos: 'Photos',
  Instagram: 'Instagram',
  Bio: 'Bio',
  'Change my cover photo': 'Changer ma photo de couverture',
  'Cover photo': 'Photo de couverture',
  'Cover photo updated': 'Photo de couverture mise à jour',
  'Cover photo removed': 'Photo de couverture supprimée',
  'A wide photo — your car, a meetup. It appears at the top of your profile and of your Home screen.':
    "Une photo en largeur — votre voiture, une rencontre. Elle s'affiche en haut de votre profil et de votre écran d'accueil.",
  '@name or link': '@nom ou lien',
  'A few words about you and your car': 'Quelques mots sur vous et votre voiture',

  // ─── Bans ──────────────────────────────────────────────────────────────────
  'Banned until {date}': "Banni jusqu'au {date}",
  'Banned for life': 'Banni à vie',
  'The chat, the map and meetups are closed meanwhile. Your pass and payments stay available.':
    'Le chat, la carte et les rencontres vous sont fermés en attendant. Votre pass et vos paiements restent accessibles.',
  'Bans so far': 'Bans reçus',
  'Devices used': 'Appareils utilisés',
  Bans: 'Bans',
  'Lift the ban': 'Lever le ban',
  'Lift the ban?': 'Lever le ban ?',
  'Ban for {days} days': 'Bannir {days} jours',
  'Ban for {days} days?': 'Bannir {days} jours ?',
  'Ban no. {count} · 3, 7, then 15 days': 'Ban n° {count} · 3, 7, puis 15 jours',
  'Ban for life': 'Bannir à vie',
  'Ban for life?': 'Bannir à vie ?',
  'Signed out, sign-in refused, their phones blocked': 'Déconnecté, connexion refusée, ses téléphones bloqués',
  '{name} is signed out everywhere and can no longer sign in. No new account can be created from the phones they used.':
    "{name} est déconnecté partout et ne peut plus se connecter. Aucun nouveau compte ne pourra être créé depuis les téléphones qu'il a utilisés.",
  '{name} loses the chat, the map and meetups for {days} days, and keeps their pass and payments. The ban ends by itself.':
    '{name} perd le chat, la carte et les rencontres pendant {days} jours, et garde son pass et ses paiements. Le ban se termine tout seul.',
  Reason: 'Motif',
  'Shown to the member': 'Affiché au membre',
  '{name} gets the app back now. A ban lifted early does not count for the next one.':
    "{name} retrouve l'app tout de suite. Un ban levé avant la fin ne compte pas pour le suivant.",

  // ─── Places & red zones ────────────────────────────────────────────────────
  'Red zone': 'Zone rouge',
  'Members are never shown on the map within {meters} m of this place, so that nobody can be found there.':
    "Aucun membre n'apparaît sur la carte à moins de {meters} m de ce lieu, pour que personne ne puisse y être localisé.",
  'Red zone “{name}”: sharing is paused, the other members do not see you. It resumes when you leave.':
    'Zone rouge « {name} » : partage en pause, les autres membres ne vous voient pas. Il reprend à la sortie de la zone.',
  'Places & red zones': 'Lieux et zones rouges',
  'Club places': 'Lieux du club',
  'Map results': 'Résultats de la carte',
  'On the members’ map': 'Sur la carte des membres',
  '{places} places · {zones} red zones': '{places} lieux · {zones} zones rouges',
  'Tap the map to add a place or a red zone. Tap one to change or remove it.':
    'Touchez la carte pour ajouter un lieu ou une zone rouge. Touchez-en un pour le modifier ou le supprimer.',
  'Place saved': 'Lieu enregistré',
  'Red zone saved': 'Zone rouge enregistrée',
  'Place removed': 'Lieu supprimé',
  'Red zone removed': 'Zone rouge supprimée',
  Place: 'Lieu',
  'Add here': 'Ajouter ici',
  'What to add': 'Que voulez-vous ajouter',
  'e.g. Residential streets': 'ex. Quartier résidentiel',
  Category: 'Catégorie',
  'Description (optional): opening hours, discount for members…': 'Description (facultatif) : horaires, remise pour les membres…',
  'Description (optional): why this zone, what to do there…': 'Description (facultatif) : pourquoi cette zone, comment s’y comporter…',
  Radius: 'Rayon',
  'Members inside are hidden from the map, and see a red alert while there. Every member sees the zone.':
    "Les membres à l'intérieur sont masqués sur la carte et voient une alerte rouge tant qu'ils y sont. Tous les membres voient la zone.",
  'Add the place': 'Ajouter le lieu',
  'Add the red zone': 'Ajouter la zone rouge',
  'Remove the place': 'Supprimer le lieu',
  'Remove the red zone': 'Supprimer la zone rouge',

  // ─── Pass, membership, payments ────────────────────────────────────────────
  Membership: 'Adhésion',
  'Member pass': 'Pass membre',
  'Pass QR code': 'QR code du pass',
  'Show this code to an organizer at check-in. It refreshes every few minutes.':
    "Montrez ce code à un organisateur à l'entrée. Il se renouvelle toutes les quelques minutes.",
  Fees: 'Tarifs',
  Dues: 'Cotisation',
  'Entry fee': "Frais d'entrée",
  'One time': 'Une seule fois',
  '{amount} / year': '{amount} / an',
  'Pay the entry fee': "Payer les frais d'entrée",
  'Pay dues': 'Payer la cotisation',
  'Pay dues in advance': "Payer la cotisation à l'avance",
  Payments: 'Paiements',
  Payment: 'Paiement',
  'Your payments and their status will show up here.': 'Vos paiements et leur statut apparaîtront ici.',
  'Payment withdrawn': 'Paiement retiré',
  'Withdraw this payment': 'Retirer ce paiement',
  Verified: 'Vérifié',
  Rejected: 'Refusé',
  'Proof uploaded · awaiting treasurer': 'Justificatif envoyé · en attente du trésorier',
  'Pay in person · awaiting treasurer': 'Paiement en main propre · en attente du trésorier',
  'Paid in person': 'Payé en main propre',
  Amount: 'Montant',
  Method: 'Mode',
  'Proof upload': 'Justificatif envoyé',
  'In person': 'En main propre',
  Submitted: 'Envoyé le',
  "Treasurer's note": 'Note du trésorier',
  'Open the proof in full size': 'Ouvrir le justificatif en grand',
  'Open the proof (PDF)': 'Ouvrir le justificatif (PDF)',
  'Open the proof (file)': 'Ouvrir le justificatif (fichier)',
  'Your entry fee is awaiting the treasurer. You will be notified as soon as it is verified.':
    "Vos frais d'entrée sont en attente du trésorier. Vous serez prévenu dès leur vérification.",
  'View my payments': 'Voir mes paiements',
  'There is nothing to pay right now.': "Il n'y a rien à payer pour le moment.",
  'Proof sent. The treasurer will verify it.': 'Justificatif envoyé. Le trésorier va le vérifier.',
  'Noted. Pay the treasurer at the next meetup.': "C'est noté. Payez le trésorier à la prochaine rencontre.",
  'This file is larger than 8 MB. Take a screenshot or a smaller photo instead.':
    "Ce fichier dépasse 8 Mo. Faites plutôt une capture d'écran ou une photo plus légère.",
  'Request sent! Next step: pay the entry fee. Your badge is issued once the treasurer confirms it.':
    "Demande envoyée ! Étape suivante : payer les frais d'entrée. Votre badge est émis dès que le trésorier confirme.",
  'What you pay': 'Ce que vous payez',
  How: 'Comment',
  'Payment method': 'Mode de paiement',
  'Upload a proof': 'Envoyer un justificatif',
  'Pay in person': 'Payer en main propre',
  'Add a photo or PDF': 'Ajouter une photo ou un PDF',
  'Transfer receipt, D17 screenshot… max 8 MB': 'Reçu de virement, capture D17… 8 Mo max',
  'Reference (optional)': 'Référence (facultatif)',
  'Hand {amount} to the treasurer or an organizer, for example at the next meetup. They confirm it in the app and you get notified.':
    "Remettez {amount} au trésorier ou à un organisateur, par exemple à la prochaine rencontre. Il le confirme dans l'application et vous êtes prévenu.",
  'How to pay': 'Comment payer',
  'Send proof · {amount}': 'Envoyer le justificatif · {amount}',
  "I'll pay in person": 'Je paierai en main propre',
  '{count} period': '{count} période',
  '{count} periods': '{count} périodes',

  // ─── Meetups ───────────────────────────────────────────────────────────────
  Meetup: 'Rencontre',
  'New meetup': 'Nouvelle rencontre',
  Upcoming: 'À venir',
  Past: 'Passées',
  'Nothing planned yet': 'Rien de prévu pour le moment',
  'No past meetups': 'Aucune rencontre passée',
  'You will be notified when the organizers plan a meetup.': 'Vous serez prévenu quand les organisateurs planifieront une rencontre.',
  'Visible to Organizer role and above': 'Visible à partir du rôle Organisateur',
  'Meeting point on the map': 'Lieu de rendez-vous sur la carte',
  'Meeting point to be announced': 'Lieu de rendez-vous à annoncer',
  'Meeting point revealed to confirmed members {count} hour before start.':
    'Lieu de rendez-vous révélé aux membres confirmés {count} heure avant le début.',
  'Meeting point revealed to confirmed members {count} hours before start.':
    'Lieu de rendez-vous révélé aux membres confirmés {count} heures avant le début.',
  'Location revealed {hours}h before': 'Lieu révélé {hours} h avant',
  'Confirm you are going to see the spot': 'Confirmez votre participation pour voir le lieu',
  Cancelled: 'Annulée',
  '{count} went': '{count} présents',
  '{count} going': '{count} participants',
  Going: 'Participants',
  Went: 'Présents',
  RSVP: 'Participer',
  "You're going. See you there!": 'Vous participez. À bientôt sur place !',
  'RSVP cancelled': 'Participation annulée',
  'On the map': 'Sur la carte',
  Hidden: 'Caché',
  'Confirm to unlock': 'Confirmez pour débloquer',
  'To be announced': 'À annoncer',
  'Hosted by {name}': 'Organisée par {name}',
  'Identity meetup': 'Rencontre Identity',
  Date: 'Date',
  Start: 'Début',
  'Meeting point': 'Lieu de rendez-vous',
  'Who can join': 'Qui peut venir',
  'Members who confirmed see the meeting point from {date} at {time}.':
    'Les membres confirmés voient le lieu de rendez-vous à partir du {date} à {time}.',
  About: 'À propos',
  'Meet rules': 'Règles de la rencontre',
  'This meetup has ended': 'Cette rencontre est terminée',
  "You're going": 'Vous participez',
  "I'm going": 'Je participe',
  'Edit meetup': 'Modifier la rencontre',
  "Can't make it?": 'Un empêchement ?',
  'You will no longer see the meeting point once it is revealed.':
    'Vous ne verrez plus le lieu de rendez-vous une fois révélé.',
  'The organizers will see one person less.': 'Les organisateurs verront une personne de moins.',
  'Cancel my RSVP': 'Annuler ma participation',
  Directions: 'Itinéraire',
  'Location locked': 'Lieu verrouillé',
  'Unlocks {date} at {time}': 'Débloqué le {date} à {time}',
  "Tap “I'm going” to unlock the meeting point": 'Touchez « Je participe » pour débloquer le lieu de rendez-vous',
  'You will see it here': 'Vous le verrez ici',
  'Meetup updated': 'Rencontre mise à jour',
  'Meetup created. Members are notified.': 'Rencontre créée. Les membres sont prévenus.',
  'Meetup cancelled. Attendees are notified.': 'Rencontre annulée. Les participants sont prévenus.',
  'Everyone in the club, pending members included. The meeting point is visible to all.':
    'Tout le club, membres en attente compris. Le lieu de rendez-vous est visible par tous.',
  'Active members only. The meeting point stays hidden until {hours} h before the start, and only members who confirmed see it.':
    "Membres actifs uniquement. Le lieu de rendez-vous reste caché jusqu'à {hours} h avant le début, et seuls les membres confirmés le voient.",
  'Organizers, treasurer and admins only.': 'Organisateurs, trésorier et admins uniquement.',
  Title: 'Titre',
  Visibility: 'Visibilité',
  'Reveal spot': 'Révéler le lieu',
  '{hours} h before': '{hours} h avant',
  Spot: 'Lieu',
  'e.g. Lac 2 parking': 'ex. Parking du Lac 2',
  Address: 'Adresse',
  Details: 'Détails',
  Description: 'Description',
  'The plan: route, dress code, what to bring…': 'Le programme : trajet, tenue, quoi apporter…',
  'Save changes': 'Enregistrer les modifications',
  'Create meetup': 'Créer la rencontre',
  'Cancel meetup': 'Annuler la rencontre',
  'Cancel this meetup?': 'Annuler cette rencontre ?',
  'Members who are going will be notified. This cannot be undone.':
    'Les participants seront prévenus. Cette action est définitive.',

  // ─── Map ───────────────────────────────────────────────────────────────────
  'Map layer': 'Calque de la carte',
  Members: 'Membres',
  'Show my position': 'Afficher ma position',
  'Members here': 'Membres ici',
  'Search members or places': 'Rechercher un membre ou un lieu',
  'Clear search': 'Effacer la recherche',
  'Keep typing…': 'Continuez à écrire…',
  Places: 'Lieux',
  'Searching…': 'Recherche…',
  'No place found.': 'Aucun lieu trouvé.',
  'Share my location': 'Partager ma position',
  'On · approximate position (about {meters} m)': 'Activé · position approximative (environ {meters} m)',
  'Off · you are hidden from the map': "Désactivé · vous n'apparaissez pas sur la carte",
  'Visible to active members only · never your exact address': 'Visible par les membres actifs uniquement · jamais votre adresse exacte',
  'Member map': 'Carte des membres',
  'Opens once your membership is active': "S'ouvre une fois votre adhésion active",
  'Active members see each other here and can share their approximate position.':
    'Les membres actifs se voient ici et peuvent partager leur position approximative.',
  'View my membership': 'Voir mon adhésion',
  'Position shared': 'Position partagée',
  'Approximate position (about {meters} m). Never share it outside the club.':
    'Position approximative (environ {meters} m). Ne la partagez jamais en dehors du club.',
  '{count} member': '{count} membre',
  '{count} members': '{count} membres',
  'Could not get your position.': "Impossible d'obtenir votre position.",
  'Search a place or address': 'Rechercher un lieu ou une adresse',
  'Search a place': 'Rechercher un lieu',
  'Use my position': 'Utiliser ma position',
  'Remove pin': 'Retirer le repère',
  'Location permission is off. Allow it in your browser or phone settings.':
    "L'accès à la position est désactivé. Autorisez-le dans les réglages de votre navigateur ou de votre téléphone.",
  'Your position is unavailable right now.': 'Votre position est indisponible pour le moment.',
  'Finding your position took too long. Try again.': 'La recherche de votre position a pris trop de temps. Réessayez.',
  'This device cannot share its location.': 'Cet appareil ne peut pas partager sa position.',

  // ─── Pass check ────────────────────────────────────────────────────────────
  'Pass check': 'Contrôle du pass',
  'Only organizers can check passes. Your own pass is in your account.':
    'Seuls les organisateurs peuvent contrôler les pass. Votre pass se trouve dans votre compte.',
  "This link does not contain a pass. Scan the member's QR code again.":
    'Ce lien ne contient pas de pass. Scannez à nouveau le QR code du membre.',
  'Scan another pass': 'Scanner un autre pass',
  'Valid member': 'Membre valide',
  'Not valid': 'Non valide',
  'Scan a pass': 'Scanner un pass',
  'This browser cannot open the camera. Use the phone camera app to scan the code instead.':
    "Ce navigateur ne peut pas ouvrir la caméra. Scannez plutôt le code avec l'appareil photo du téléphone.",
  'Camera access was refused. Allow the camera for this site, or scan with the phone camera app.':
    "L'accès à la caméra a été refusé. Autorisez la caméra pour ce site, ou scannez avec l'appareil photo du téléphone.",
  Camera: 'Caméra',
  "Point the camera at the member's pass QR code.": 'Visez le QR code du pass du membre.',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'Pending requests': 'Demandes en attente',
  'To do': 'À faire',
  'Payments to review': 'Paiements à vérifier',
  'Membership requests': "Demandes d'adhésion",
  'Membership request': "Demande d'adhésion",
  'Check-in at a meetup': "Contrôle à l'entrée d'une rencontre",
  'Club settings': 'Réglages du club',
  'Fees, dues period, rules': 'Tarifs, période de cotisation, règles',
  'API documentation': "Documentation de l'API",
  'For developers of the club apps': 'Pour les développeurs des applications du club',
  All: 'Tous',
  Staff: 'Équipe',
  'Name or phone': 'Nom ou téléphone',
  'Search members': 'Rechercher des membres',
  Filter: 'Filtre',
  'No member matches.': 'Aucun membre ne correspond.',
  '{count} to review': '{count} à vérifier',
  proof: 'justificatif',
  'in person': 'en main propre',
  'To review': 'À vérifier',
  History: 'Historique',
  'No payments yet': 'Aucun paiement pour le moment',
  'New proofs and in-person payments to confirm will show up here.':
    'Les nouveaux justificatifs et paiements en main propre à confirmer apparaîtront ici.',
  Requested: 'Demande du',
  'Location sharing': 'Partage de position',
  Actions: 'Actions',
  Call: 'Appeler',
  'Record a payment in person': 'Enregistrer un paiement en main propre',
  'Approve without payment': 'Approuver sans paiement',
  'Founding members, honorary badges…': "Membres fondateurs, badges d'honneur…",
  'Change role': 'Changer le rôle',
  'Temporary password': 'Mot de passe temporaire',
  'When the member forgot theirs': 'Quand le membre a oublié le sien',
  Suspend: 'Suspendre',
  'Lift the suspension': 'Lever la suspension',
  'Decline the request': 'Refuser la demande',
  'No payment yet.': 'Aucun paiement pour le moment.',
  'Approve without payment?': 'Approuver sans paiement ?',
  'The member gets a badge and full access now. The entry fee will not be recorded.':
    "Le membre obtient un badge et un accès complet dès maintenant. Les frais d'entrée ne seront pas enregistrés.",
  Approve: 'Approuver',
  'Member approved': 'Membre approuvé',
  'Lift the suspension?': 'Lever la suspension ?',
  'The member gets access to the map and member meetups again.':
    'Le membre retrouve son accès à la carte et aux rencontres entre membres.',
  'Lift suspension': 'Lever la suspension',
  'Suspension lifted': 'Suspension levée',
  'Suspend this member?': 'Suspendre ce membre ?',
  'They lose access to the member map and member-only meetups, and their shared position is deleted.':
    'Il perd son accès à la carte des membres et aux rencontres réservées aux membres, et sa position partagée est supprimée.',
  'Member suspended': 'Membre suspendu',
  'Decline this request?': 'Refuser cette demande ?',
  'Pending payments of this request are rejected too.': 'Les paiements en attente de cette demande sont refusés aussi.',
  Decline: 'Refuser',
  'Request declined': 'Demande refusée',
  'Role updated': 'Rôle mis à jour',
  'Map, meetups, pass': 'Carte, rencontres, pass',
  'Creates meetups, posts announcements, checks passes': 'Crée des rencontres, publie des annonces, contrôle les pass',
  'Verifies payments, records cash': 'Vérifie les paiements, enregistre les espèces',
  'Everything, including roles and club settings': 'Tout, y compris les rôles et les réglages du club',
  'Payment in person': 'Paiement en main propre',
  'What was paid': 'Ce qui a été payé',
  'e.g. Cash at Coffee & Cars': 'ex. Espèces au Coffee & Cars',
  'Record as received': 'Enregistrer comme reçu',
  Copied: 'Copié',
  Copy: 'Copier',
  'Select the password to copy it': 'Sélectionnez le mot de passe pour le copier',
  'Give this password to {name}. It is shown only once; they can change it from their account.':
    "Donnez ce mot de passe à {name}. Il n'est affiché qu'une fois ; il pourra le changer depuis son compte.",
  '{name} will be signed out on every device and will sign in with a new password you give them.':
    '{name} sera déconnecté de tous ses appareils et se connectera avec le nouveau mot de passe que vous lui donnerez.',
  'Create a temporary password': 'Créer un mot de passe temporaire',
  'Payment verified. The member is notified.': 'Paiement vérifié. Le membre est prévenu.',
  'Payment rejected': 'Paiement refusé',
  'Note to the member': 'Note pour le membre',
  'Note to the member (optional, e.g. why it is rejected)': 'Note pour le membre (facultatif, ex. raison du refus)',
  Reject: 'Refuser',
  Received: 'Reçu',
  Verify: 'Vérifier',
  'Settings saved': 'Réglages enregistrés',
  'Entry fee: enter an amount such as 20 or 7.5': "Frais d'entrée : saisissez un montant comme 20 ou 7,5",
  'Dues: enter an amount such as 5 or 7.5': 'Cotisation : saisissez un montant comme 5 ou 7,5',
  'Dues period': 'Période de cotisation',
  Currency: 'Devise',
  'Grace period': 'Délai de grâce',
  days: 'jours',
  'Entry fee covers the first period': "Les frais d'entrée couvrent la première période",
  'Periods follow the calendar (with 3 months: Q1 Jan–Mar, Q2 Apr–Jun…). New amounts apply to payments submitted from now on. Members keep access during the grace period after their dues end.':
    "Les périodes suivent le calendrier (pour 3 mois : T1 janv.–mars, T2 avr.–juin…). Les nouveaux montants s'appliquent aux paiements envoyés à partir de maintenant. Les membres gardent leur accès pendant le délai de grâce après la fin de leur cotisation.",
  'Meetups & map': 'Rencontres et carte',
  'h before start': 'h avant le début',
  'Map positions': 'Positions sur la carte',
  'hours max': 'heures max',
  'Payment instructions': 'Instructions de paiement',
  'Shown when members upload a proof: bank account, D17 number, who to pay in person…':
    'Affiché quand un membre envoie un justificatif : compte bancaire, numéro D17, à qui payer en main propre…',
  'Start a section with “# Title” and each rule with “- ”.': 'Commencez une section par « # Titre » et chaque règle par « - ».',
  'Default meet rules': 'Règles de rencontre par défaut',
  'These three texts are shown as you write them. While you leave them unchanged, members see them in their own language.':
    'Ces trois textes sont affichés tels que vous les écrivez. Tant que vous ne les modifiez pas, les membres les voient dans leur langue.',
  'Save settings': 'Enregistrer les réglages',
};
